import React, { useMemo, useState } from 'react';
import { FamilyTreeNode, PublishedFamilyTreeRecord } from '../../types/familyTree';
import FamilyTreeMemberModal from './FamilyTreeMemberModal';
import { displayRelationLabel, formatSimpleRelationLabel, hasConditionOnRecord } from '../../utils/familyTreeUtils';

interface FamilyTreeCanvasProps {
  tree: PublishedFamilyTreeRecord | null;
  /** When false, Melio record panel is blurred in the member detail modal. */
  melioNotesUnlocked?: boolean;
  compact?: boolean;
  expanded?: boolean;
  selfLabel?: string;
  canEditPersonalNotes?: boolean;
  onPersonalNotesChange?: (nodeId: string, notes: string) => void;
  /** @deprecated Tree is always visible; use melioNotesUnlocked instead. */
  locked?: boolean;
  overlayTitle?: string;
  overlayBody?: string;
  /** @deprecated Use single-click modal instead. */
  onNodeDoubleClick?: (node: FamilyTreeNode) => void;
}

const PLACEHOLDER_NODES: FamilyTreeNode[] = [
  {
    id: 'maternal-grandmother',
    relationKey: 'grandmother',
    relationLabel: 'grandmother',
    lineageSide: 'maternal',
    generation: 0,
    branch: 'maternal',
    aliveStatus: 'unknown',
    healthSignal: 'unknown',
    caption: '',
    conditions: [],
  },
  {
    id: 'mother-branch',
    relationKey: 'mother',
    relationLabel: 'mother',
    lineageSide: 'maternal',
    generation: 1,
    branch: 'maternal',
    aliveStatus: 'unknown',
    healthSignal: 'monitored',
    caption: '',
    conditions: [],
  },
  {
    id: 'member-self',
    relationKey: 'self',
    relationLabel: 'you',
    lineageSide: 'self',
    generation: 2,
    branch: 'self',
    aliveStatus: 'alive',
    healthSignal: 'healthy',
    caption: '',
    conditions: [],
  },
  {
    id: 'father-branch',
    relationKey: 'father',
    relationLabel: 'father',
    lineageSide: 'paternal',
    generation: 1,
    branch: 'paternal',
    aliveStatus: 'unknown',
    healthSignal: 'monitored',
    caption: '',
    conditions: [],
  },
  {
    id: 'paternal-grandfather',
    relationKey: 'grandfather',
    relationLabel: 'grandfather',
    lineageSide: 'paternal',
    generation: 0,
    branch: 'paternal',
    aliveStatus: 'unknown',
    healthSignal: 'unknown',
    caption: '',
    conditions: [],
  },
  {
    id: 'siblings-branch',
    relationKey: 'sister',
    relationLabel: 'sister',
    lineageSide: 'shared',
    generation: 2,
    branch: 'siblings',
    aliveStatus: 'unknown',
    healthSignal: 'unknown',
    caption: '',
    conditions: [],
  },
  {
    id: 'children-branch',
    relationKey: 'child',
    relationLabel: 'child',
    lineageSide: 'shared',
    generation: 3,
    branch: 'children',
    aliveStatus: 'unknown',
    healthSignal: 'unknown',
    caption: '',
    conditions: [],
  },
];

const PLACEHOLDER_EDGES = [
  { from: 'maternal-grandmother', to: 'mother-branch' },
  { from: 'mother-branch', to: 'member-self' },
  { from: 'father-branch', to: 'member-self' },
  { from: 'paternal-grandfather', to: 'father-branch' },
  { from: 'siblings-branch', to: 'member-self' },
  { from: 'children-branch', to: 'member-self' },
];

/* ── Pedigree (clinical genogram) helpers ──────────────────────────────────
   Square = male · Circle = female · Diamond = unknown sex.
   Stroke colour carries the health signal; a filled shape / centre pip marks a
   condition on record; a centre dot marks deceased. */
type Sex = 'male' | 'female' | 'unknown';

// Keys arrive as bare ("mother") or prefixed ("maternal_grandmother", "paternal_uncle"),
// so we match on substrings. "grandmother" contains "mother" → female; "grandfather"
// contains "father" → male, which keeps the precedence correct.
const FEMALE_TOKENS = ['mother', 'sister', 'aunt', 'daughter', 'niece', 'wife', 'female'];
const MALE_TOKENS = ['father', 'brother', 'uncle', 'son', 'nephew', 'husband', 'male'];

function sexOfRelation(relationKey: string): Sex {
  const key = (relationKey || '').toLowerCase();
  if (FEMALE_TOKENS.some((token) => key.includes(token))) return 'female';
  if (MALE_TOKENS.some((token) => key.includes(token))) return 'male';
  return 'unknown';
}

const SIGNAL_COLOR: Record<FamilyTreeNode['healthSignal'], { stroke: string; fill: string }> = {
  healthy: { stroke: '#4F5D18', fill: '#FFFFFF' },
  monitored: { stroke: '#C4A035', fill: 'rgba(212, 175, 55, 0.28)' },
  affected: { stroke: '#C4A035', fill: 'rgba(212, 175, 55, 0.42)' },
  unknown: { stroke: '#DCDAD3', fill: '#FFFFFF' },
};

const CONNECTOR = 'rgba(44, 41, 38, 0.42)';

export default function FamilyTreeCanvas({
  tree,
  melioNotesUnlocked: melioNotesUnlockedProp,
  locked: _locked,
  compact = false,
  expanded = false,
  selfLabel,
  canEditPersonalNotes = false,
  onPersonalNotesChange,
  onNodeDoubleClick,
}: FamilyTreeCanvasProps) {
  const melioNotesUnlocked = melioNotesUnlockedProp ?? false;
  const [selectedNode, setSelectedNode] = useState<FamilyTreeNode | null>(null);
  const rawNodes = tree?.nodes?.length ? tree.nodes : PLACEHOLDER_NODES;
  const nodes = useMemo(
    () => rawNodes.map((node) => (
      (node.id === 'member-self' || node.relationKey === 'self') && selfLabel
        ? { ...node, relationLabel: selfLabel }
        : node
    )),
    [rawNodes, selfLabel],
  );
  const edges = tree?.edges?.length ? tree.edges : PLACEHOLDER_EDGES;
  const canvasWidth = compact ? 920 : expanded ? 1700 : 1200;
  const canvasHeight = compact ? 560 : expanded ? 900 : 680;

  const positionedNodes = useMemo(() => {
    type PositionedNode = FamilyTreeNode & { x: number; y: number };
    const positioned = new Map<string, PositionedNode>();
    const sortedNodes = [...nodes];
    const sidePadding = compact ? 80 : 100;
    const maternalCenter = canvasWidth * 0.24;
    const paternalCenter = canvasWidth * 0.76;
    const centerX = canvasWidth / 2;
    const rowTop = compact ? 88 : 100;
    const rowMid = compact ? 200 : 250;
    const rowSelf = compact ? 310 : 390;
    const rowCousins = compact ? 410 : 560;
    const rowChildren = compact ? 500 : 680;
    const spacing = compact ? 108 : 148;

    const sortByPriority = (a: FamilyTreeNode, b: FamilyTreeNode) => {
      const priority: Record<string, number> = {
        grandmother: 1,
        grandfather: 2,
        mother: 1,
        father: 1,
        aunt: 2,
        uncle: 3,
        brother: 1,
        sister: 2,
        self: 3,
        child: 4,
        cousin: 5,
        other: 6,
      };
      return (priority[a.relationKey] ?? 99) - (priority[b.relationKey] ?? 99);
    };

    const placeGroup = (group: FamilyTreeNode[], center: number, y: number) => {
      const ordered = [...group].sort(sortByPriority);
      const startX = center - ((ordered.length - 1) * spacing) / 2;
      ordered.forEach((node, index) => {
        positioned.set(node.id, {
          ...node,
          x: Math.max(sidePadding, Math.min(canvasWidth - sidePadding, startX + index * spacing)),
          y,
        });
      });
    };

    const maternalGrandparents = sortedNodes.filter((node) => node.lineageSide === 'maternal' && node.generation === 0);
    const paternalGrandparents = sortedNodes.filter((node) => node.lineageSide === 'paternal' && node.generation === 0);
    const maternalAdults = sortedNodes.filter((node) => node.lineageSide === 'maternal' && node.generation === 1);
    const paternalAdults = sortedNodes.filter((node) => node.lineageSide === 'paternal' && node.generation === 1);
    const siblings = sortedNodes.filter((node) => node.branch === 'siblings' || node.relationKey === 'brother' || node.relationKey === 'sister');
    const selfNodes = sortedNodes.filter((node) => node.branch === 'self' || node.relationKey === 'self');
    const children = sortedNodes.filter((node) => node.branch === 'children' || node.relationKey === 'child');
    const anchoredCousins = sortedNodes.filter((node) => node.relationKey === 'cousin' && node.connectedToNodeId);
    const floatingCousins = sortedNodes.filter((node) => node.relationKey === 'cousin' && !node.connectedToNodeId);
    const ungrouped = sortedNodes.filter((node) =>
      !maternalGrandparents.includes(node) &&
      !paternalGrandparents.includes(node) &&
      !maternalAdults.includes(node) &&
      !paternalAdults.includes(node) &&
      !siblings.includes(node) &&
      !selfNodes.includes(node) &&
      !children.includes(node) &&
      !anchoredCousins.includes(node) &&
      !floatingCousins.includes(node),
    );

    placeGroup(maternalGrandparents, maternalCenter, rowTop);
    placeGroup(paternalGrandparents, paternalCenter, rowTop);
    placeGroup(maternalAdults, maternalCenter, rowMid);
    placeGroup(paternalAdults, paternalCenter, rowMid);
    placeGroup(siblings, centerX - (compact ? 140 : 170), rowSelf);
    placeGroup(selfNodes, centerX, rowSelf);
    placeGroup(children, centerX, rowChildren);

    const cousinGroups = new Map<string, FamilyTreeNode[]>();
    anchoredCousins.forEach((node) => {
      const key = node.connectedToNodeId || 'floating';
      cousinGroups.set(key, [...(cousinGroups.get(key) || []), node]);
    });

    cousinGroups.forEach((group, anchorId) => {
      const anchor = positioned.get(anchorId);
      if (!anchor) return;
      placeGroup(group, anchor.x, rowCousins);
    });

    if (floatingCousins.length > 0) {
      placeGroup(floatingCousins, centerX, rowCousins);
    }

    if (ungrouped.length > 0) {
      placeGroup(ungrouped, centerX + (compact ? 150 : 190), rowSelf);
    }

    return Array.from(positioned.values());
  }, [canvasWidth, compact, nodes]);

  const positionedNodeMap = useMemo(
    () => new Map(positionedNodes.map((node) => [node.id, node])),
    [positionedNodes],
  );

  // Couple bars (horizontal) — grandparents on each side, and the parents.
  const coupleLines: Array<[{ x: number; y: number }, { x: number; y: number }]> = [];
  const pushPair = (group: typeof positionedNodes) => {
    if (group.length >= 2) {
      const ordered = [...group].sort((a, b) => a.x - b.x);
      coupleLines.push([ordered[0], ordered[ordered.length - 1]]);
    }
  };
  pushPair(positionedNodes.filter((n) => n.lineageSide === 'maternal' && n.generation === 0));
  pushPair(positionedNodes.filter((n) => n.lineageSide === 'paternal' && n.generation === 0));
  pushPair(positionedNodes.filter((n) => n.relationKey === 'mother' || n.relationKey === 'father'));

  const nodeR = compact ? 16 : expanded ? 24 : 21;
  const labelSize = compact ? 10.5 : 12;
  const labelDy = nodeR + (compact ? 15 : 18);

  const openNode = (node: FamilyTreeNode) => {
    if (onNodeDoubleClick) {
      onNodeDoubleClick(node);
      return;
    }
    setSelectedNode(node);
  };

  // Trim empty vertical space: size the viewBox to the lowest node's label.
  const contentBottom = positionedNodes.reduce((max, n) => Math.max(max, n.y), 0);
  const contentTop = positionedNodes.reduce((min, n) => Math.min(min, n.y), canvasHeight);
  const topPad = nodeR + (compact ? 22 : 30);
  const viewTop = Math.max(0, contentTop - topPad);
  const viewHeight = Math.max(120, contentBottom + labelDy + (compact ? 26 : 40) - viewTop);

  return (
    <div className={`family-tree-stage ${expanded ? 'family-tree-stage-expanded' : ''}`}>
      <div
        className={`family-tree-canvas ${compact ? 'family-tree-canvas-compact' : ''} ${expanded ? 'family-tree-canvas-expanded' : ''}`}
        style={{ width: '100%', minHeight: 'auto', overflow: 'hidden' }}
      >
        <div className="family-tree-orb family-tree-orb-left" />
        <div className="family-tree-orb family-tree-orb-right" />
        <div className="family-tree-orb family-tree-orb-bottom" />

        <svg
          viewBox={`0 ${viewTop} ${canvasWidth} ${viewHeight}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', width: '100%', height: 'auto', position: 'relative', zIndex: 1 }}
          role="img"
          aria-label="Family pedigree"
        >
          {/* descent lines (drawn first, hidden under the shapes) */}
          {edges.map((edge) => {
            const fromNode = positionedNodeMap.get(edge.from);
            const toNode = positionedNodeMap.get(edge.to);
            if (!fromNode || !toNode) return null;
            const midY = (fromNode.y + toNode.y) / 2;
            return (
              <path
                key={`${edge.from}-${edge.to}`}
                d={`M ${fromNode.x} ${fromNode.y} V ${midY} H ${toNode.x} V ${toNode.y}`}
                fill="none"
                stroke={CONNECTOR}
                strokeWidth={1.4}
              />
            );
          })}

          {/* couple bars */}
          {coupleLines.map(([a, b], index) => (
            <line key={`couple-${index}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={CONNECTOR} strokeWidth={1.4} />
          ))}

          {/* genogram shapes */}
          {positionedNodes.map((node) => {
            const isSelf = node.id === 'member-self' || node.relationKey === 'self';
            const sex = sexOfRelation(node.relationKey);
            const sig = SIGNAL_COLOR[node.healthSignal] || SIGNAL_COLOR.unknown;
            const fill = isSelf ? '#3D0B0D' : sig.fill;
            const stroke = isSelf ? '#3D0B0D' : sig.stroke;
            const label = isSelf ? displayRelationLabel(node) : formatSimpleRelationLabel(node.relationKey, node.relationLabel);
            const { x, y } = node;
            const r = nodeR;

            let shape: React.ReactNode;
            if (sex === 'male') {
              shape = <rect x={x - r} y={y - r} width={r * 2} height={r * 2} fill={fill} stroke={stroke} strokeWidth={2.5} />;
            } else if (sex === 'female') {
              shape = <circle cx={x} cy={y} r={r} fill={fill} stroke={stroke} strokeWidth={2.5} />;
            } else {
              shape = (
                <path
                  d={`M ${x} ${y - r - 2} L ${x + r + 2} ${y} L ${x} ${y + r + 2} L ${x - r - 2} ${y} Z`}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={2.5}
                />
              );
            }

            return (
              <g
                key={node.id}
                style={{ cursor: 'pointer' }}
                onClick={() => openNode(node)}
                onDoubleClick={() => openNode(node)}
              >
                <title>Click for notes</title>
                {shape}
                {hasConditionOnRecord(node) && !isSelf && (
                  <circle cx={x} cy={y} r={Math.max(2.5, r * 0.18)} fill="#9A7B1A" />
                )}
                {node.aliveStatus === 'deceased' && (
                  <circle
                    cx={x}
                    cy={y}
                    r={Math.max(2.5, r * 0.22)}
                    fill={isSelf ? '#F7F6F2' : 'var(--b-black)'}
                  />
                )}
                {isSelf && (
                  <text x={x} y={y + r * 0.3} textAnchor="middle" fill="#F7F6F2" fontFamily="var(--melio-quote)" fontSize={r * 0.85}>
                    You
                  </text>
                )}
                <text x={x} y={y + labelDy} textAnchor="middle" fill="var(--b-black)" fontFamily="var(--melio-sans)" fontSize={labelSize} fontWeight={isSelf ? 600 : 500}>
                  {label}
                </text>
              </g>
            );
          })}
        </svg>

        {(compact || !expanded) && (
          <div
            className="family-tree-legend"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.25rem',
              padding: '0.85rem 1rem 1rem',
              fontFamily: "var(--melio-sans)",
              fontSize: '0.72rem',
              color: '#6B6862',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, border: '1.5px solid var(--b-black)' }} /> Male
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid var(--b-black)' }} /> Female
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, border: '1.5px solid #4F5D18', background: '#FFFFFF' }} /> No concerns noted
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, border: '1.5px solid #C4A035', background: 'rgba(212, 175, 55, 0.42)' }} /> Condition on record
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, borderRadius: '50%', border: '1.5px dashed #DCDAD3' }} /> Unknown / not asked
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  border: '1.5px solid var(--b-black)',
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--b-black)' }} />
              </span>{' '}
              Deceased
            </span>
            <span style={{ flexBasis: '100%', fontSize: '0.68rem', color: '#8A8680' }}>
              Click any family member for personal notes and Melio record.
            </span>
          </div>
        )}

        {selectedNode && !onNodeDoubleClick && (
          <FamilyTreeMemberModal
            node={selectedNode}
            melioNotesUnlocked={Boolean(melioNotesUnlocked)}
            canEditPersonalNotes={canEditPersonalNotes}
            onClose={() => setSelectedNode(null)}
            onPersonalNotesChange={onPersonalNotesChange}
          />
        )}
      </div>
    </div>
  );
}
